import { ApiError, NetworkError } from '@/api/client';
import type { TranslateFn } from '@/i18n/LanguageContext';
import type { TranslationKey } from '@/i18n/dictionaries/en';
import type { OfficeRole } from '@/api/types';

/**
 * ADR-084 helpers shared by the staff screens. The role summaries mirror the
 * server's permission matrix (packages/foundation/src/authz/permissions.ts) -
 * they explain it, they never decide anything.
 */
export const ROLE_HELP: Record<OfficeRole, TranslationKey> = {
  ADMIN: 'staff.roleHelp.ADMIN',
  WAREHOUSE: 'staff.roleHelp.WAREHOUSE',
  PICKER: 'staff.roleHelp.PICKER',
  PACKER: 'staff.roleHelp.PACKER',
  BILLING: 'staff.roleHelp.BILLING',
  SUPPORT: 'staff.roleHelp.SUPPORT',
};

/**
 * The secret goes in the URL FRAGMENT: browsers never send `#...` to a server,
 * so it cannot land in access logs, proxies or analytics.
 */
export function oneTimeUrl(kind: 'invite' | 'recover', token: string): string {
  return `${window.location.origin}/${kind}#${token}`;
}

/** Reads the secret from `#...` and immediately removes it from the address bar and history. */
export function takeTokenFromHash(): string {
  const token = window.location.hash.replace(/^#/, '');
  if (token) window.history.replaceState(null, '', window.location.pathname);
  return token;
}

export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export function describeStaffError(t: TranslateFn, err: unknown): string | null {
  if (err instanceof NetworkError) return t('staff.err.network');
  if (!(err instanceof ApiError)) return null;
  const p = err.params as Record<string, string | undefined>;
  switch (err.code) {
    case 'AUTH.INVALID_CREDENTIALS':
      return t('staff.err.invalidCredentials');
    case 'AUTH.ACCOUNT_SUSPENDED':
      return t('staff.err.suspended');
    case 'OFFICE_AUTH.PASSWORD_WEAK':
      return t('staff.err.passwordWeak', { min: p.min ?? '10' });
    case 'OFFICE_AUTH.CURRENT_PASSWORD_WRONG':
      return t('staff.err.currentPasswordWrong');
    case 'OFFICE_AUTH.LINK_NOT_VALID':
      return t('staff.err.linkNotValid');
    case 'OFFICE_AUTH.RECOVERY_NOT_ALLOWED':
      return p.reason === 'OTHER_ORGANISATION' ? t('staff.err.recoveryOtherCompany') : t('staff.err.recoveryNotActive');
    case 'OFFICE_INVITE.ALREADY_MEMBER':
      return t('staff.err.alreadyMember');
    case 'OFFICE_INVITE.CANNOT_ACCEPT':
      return t('staff.err.cannotAccept');
    case 'MEMBERSHIP.OUT_OF_SCOPE':
      return t('staff.err.outOfScope');
    case 'MEMBERSHIP.SELF_CHANGE_FORBIDDEN':
      return t('staff.err.self');
    case 'ROLE.LAST_ADMIN':
      return t('staff.err.lastAdmin');
    case 'ENTITLEMENT.QUOTA_EXCEEDED':
      return t('staff.err.quota');
    case 'COMMON.FORBIDDEN':
      return t('staff.err.forbidden');
    case 'RATE_LIMITED':
      return t('staff.err.tooMany');
    default:
      if (err.status === 400) return t('staff.err.checkFields');
      return null;
  }
}
